import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRoles, createRole, updateRole, deleteRole as deleteRoleApi } from '../services/api';
import { showSuccessNotification, showErrorNotification } from '@core/utils/apiErrorHandler';
import type { CreateRoleRequest, UpdateRoleRequest } from '../types';

export const useRoles = () => {
  const queryClient = useQueryClient();

  const {
    data: roles = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => createRole(data),
    onSuccess: () => {
      showSuccessNotification('Role created successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to create role');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ roleId, data }: { roleId: number; data: UpdateRoleRequest }) => updateRole(roleId, data),
    onSuccess: () => {
      showSuccessNotification('Role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to update role');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId: number) => deleteRoleApi(roleId),
    onSuccess: () => {
      showSuccessNotification('Role deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (error) => {
      showErrorNotification(error, 'Failed to delete role');
    },
  });

  return {
    roles,
    isLoading,
    isError,
    error,
    refetch,
    createRole: createMutation.mutateAsync,
    updateRole: updateMutation.mutateAsync,
    deleteRole: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
